







































































































































































import { Component, Mixins, Prop, Ref, Vue } from "vue-property-decorator";
import {
  FETCH_COMMENTS,
  FETCH_CPT,
  FETCH_CPT_BASE_SCHEMA,
  FETCH_CPT_SCHEMA,
  FETCH_CPT_UI_SCHEMA,
  FETCH_COMMENT_SCHEMA,
  PROPOSE_CPT,
  UPDATE_CPT,
  ADD_COMMENT,
  DELETE_CPT,
} from "@/store/actions-types";
import {
  GET_COMMENTS,
  GET_CPT,
  GET_CPT_BASE_SCHEMA,
  GET_CPT_SCHEMA,
  GET_CPT_UI_SCHEMA,
  GET_IS_FORM_READY,
  GET_IS_DIRTY,
  GET_COMMENT_SCHEMA,
} from "@/store/getters-types";
import { CPTModel } from "@/models/cpt/CPTModel";
import {
  EMIT_NOTIFICATION,
  RESET_CPT_STATE,
  SET_ANON_AUTH_CPT_CONTEXT,
  SET_CPT,
  SET_CURRENT_AUTHENTICATED_USER,
  SET_IS_DIRTY,
} from "@/store/mutations-types";
import { LOGOUT_CURRENT_USER } from "@/store/actions-types";
import { JSONSchema6 } from "json-schema";
import CommentArea from "@/components/CommentArea.vue";
import ModelPreview from "@/components/ModelPreview.vue";
import UnauthenticatedHandler from "@/api/UnauthenticatedHandler";
import CptOptions from "@/components/CptOptions.vue";
import { NavigationGuardNext, Route } from "vue-router";
import UriPreview from "@/components/UriPreview.vue";
import { CPTCommentI } from "@/models/cpt/CPTCommentI";
import CptEditSkeleton from "@/components/CptEditSkeleton.vue";
import { RouteHelper } from "@/utils/RouteHelper";
import CPTEditModerateMixin from "@/mixins/CPTEditModerateMixin";
import { NotificationType } from "@/models/NotificationI";
import i18n from "@/plugins/i18n";
import { UserRoles } from "@/models/auth/AuthenticatedUser";

@Component({
  components: {
    CptEditSkeleton,
    CommentArea,
    ModelPreview,
    CptOptions,
    UriPreview,
  },
  /**
   * prevent leaving on unsaved changes
   */
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
    RouteHelper.canLeaveAfterSave((this as EditCPT).isDirty, to, next);
  },
})
/**
 * View to edit custom post types
 */
export default class EditCPT extends Mixins(CPTEditModerateMixin) {
  /**
   * The identifier/name of the custom post type
   */
  @Prop({ required: true, type: String }) readonly cpt!: string;

  /**
   * The id of the current contribution
   */
  @Prop({ required: true, type: String }) readonly id!: string;

  /**
   * reference to the schema form
   */
  @Ref() readonly schemaForm!: Vue;

  /**
   * true when update request is running
   */
  public isBeingUpdated = false;

  /**
   * true when proposal request is running
   */
  public isBeingProposed = false;

  /**
   * true when delete request is running
   */
  public isBeingDeleted = false;

  /**
   * get the model from the store
   */
  get model(): CPTModel {
    return this.$store.getters[GET_CPT];
  }

  /**
   * map it back to the store
   * as the store is the single
   * source of truth.
   * Necessary cause our custom components
   * need to have all details in the store
   */
  set model(updatedModel: CPTModel) {
    this.$store.commit(SET_CPT, updatedModel);
  }

  /**
   * load the json schema for the cpt
   */
  get jsonSchema(): JSONSchema6 {
    return this.$store.getters[GET_CPT_SCHEMA];
  }

  /**
   * defines if the form has been altered or not
   */
  get isDirty(): boolean {
    return this.$store.getters[GET_IS_DIRTY];
  }

  /**
   * fetch the ui schema to build
   * the form from
   */
  // eslint-disable-next-line
  get uiSchema(): any {
    return this.$store.getters[GET_CPT_UI_SCHEMA];
  }

  /**
   * comment schema getter
   */
  get commentsSchema(): JSONSchema6 {
    return this.$store.getters[GET_COMMENT_SCHEMA];
  }

  /**
   * comments getter
   */
  get comments(): CPTCommentI[] {
    return this.$store.getters[GET_COMMENTS];
  }

  /**
   * base schema getter
   */
  get baseSchema(): JSONSchema6 {
    return this.$store.getters[GET_CPT_BASE_SCHEMA];
  }

  /**
   * register window event handlers
   */
  created(): void {
    window.addEventListener("beforeunload", this.cleanUpSession);
  }

  /**
   * fetch the cpt
   */
  async mounted(): Promise<void> {
    this.$store.dispatch(FETCH_CPT_BASE_SCHEMA);
    this.$store.dispatch(FETCH_CPT_SCHEMA, this.cpt);
    this.$store.dispatch(FETCH_CPT_UI_SCHEMA, this.cpt);
    if (UnauthenticatedHandler.hasCachedCptAvailable()) {
      UnauthenticatedHandler.restoreCptData();
      this.updateCPT();
    } else {
      await this.$store.dispatch(FETCH_CPT, { id: this.id });
    }
    this.$store.dispatch(FETCH_COMMENT_SCHEMA);
    this.$store.dispatch(FETCH_COMMENTS);
  }

  /**
   * reset the cpt state when leaving this view
   */
  beforeDestroy(): void {
    this.$store.commit(RESET_CPT_STATE);
    window.removeEventListener("beforeunload", this.cleanUpSession);
  }

  /**
   * remove cookies if anon user and contribution
   * is on proposed state.
   */
  cleanUpSession(event: Event): void {
    if (this.isDirty) {
      event.preventDefault();
      event.returnValue = true;
    }
    if (this.isProposed) {
      // clear the auth state manually as we cannot wait for the logout request
      this.$store.commit(SET_CURRENT_AUTHENTICATED_USER, {
        role: UserRoles.NONE,
      });
      this.$store.commit(SET_ANON_AUTH_CPT_CONTEXT, null);
      // fire and forget here
      this.$store.dispatch(LOGOUT_CURRENT_USER);
    }
  }

  /**
   * contribution's state is proposed
   */
  get isProposed(): boolean {
    return this.model.data?.state === "proposed";
  }

  /**
   * are all resources loaded
   * to show the form?
   */
  get showForm(): boolean {
    return this.$store.getters[GET_IS_FORM_READY];
  }

  /**
   * get the number of comments
   */
  get commentsCount(): number {
    return this.$store.getters[GET_COMMENTS].length;
  }

  /**
   * indicate if comment area is shown
   */
  get showCommentArea(): boolean {
    return this.showForm && Object.keys(this.commentsSchema).length > 0;
  }

  /**
   * update the cpt
   */
  async updateCPT(): Promise<void> {
    try {
      this.isBeingUpdated = true;
      await this.$store.dispatch(UPDATE_CPT);
    } catch (e) {
      this.$store.commit(EMIT_NOTIFICATION, {
        type: NotificationType.DANGER,
        title: i18n.t("views.editCPT.updatingCptFailedTitle"),
        text: i18n.t("views.editCPT.updatingCptFailed"),
      });
    } finally {
      this.isBeingUpdated = false;
    }
  }

  /**
   * change state to in moderation
   */
  async proposeCPT(): Promise<void> {
    if (this.valid) {
      this.isBeingProposed = true;
      const success = await this.$store.dispatch(PROPOSE_CPT);
      if (success) {
        this.$store.commit(RESET_CPT_STATE);
        await this.$store.dispatch(LOGOUT_CURRENT_USER);
        await this.$router.push({
          name: "proposedCPT",
          params: { cpt: this.cpt, id: this.id },
        });
      }
      this.isBeingProposed = false;
    }
  }

  /**
   * delete the current cpt
   */
  async deleteCPT(): Promise<void> {
    this.isBeingDeleted = true;

    try {
      if (this.isDirty) {
        // allow deletions even if state is dirty
        this.$store.commit(SET_IS_DIRTY, false);
      }
      await this.$store.dispatch(DELETE_CPT);
      await this.$store.dispatch(LOGOUT_CURRENT_USER);
      this.$store.commit(EMIT_NOTIFICATION, {
        type: NotificationType.SUCCESS,
        title: this.$i18n.t("views.editCPT.deletingCPTTitle"),
        text: this.$i18n.t("views.editCPT.deletingCPT"),
      });
    } catch (e) {
      this.$store.commit(EMIT_NOTIFICATION, {
        type: NotificationType.DANGER,
        title: this.$i18n.t("views.editCPT.deletingCPTFailedTitle"),
        text: this.$i18n.t("views.editCPT.deletingCPTFailed"),
      });
    } finally {
      this.isBeingDeleted = false;
    }
  }

  /**
   * create a new comment
   * @param comment
   */
  onCommentAdded(comment: CPTCommentI): void {
    this.$store.dispatch(ADD_COMMENT, comment);
  }
}
